/*
 * Quick refills of documented medications (EXECDEV-101), and changing and
 * cancelling signed printable prescriptions (EXECDEV-176). A physician ticks
 * one or more active chart medications (or presses Refill beside one), reviews
 * the prefilled prescription in one dialog and signs it. Inside a visit it
 * signs on that encounter and can write one line per medication into the
 * Plan; outside a visit the server opens, signs and closes a "Prescription
 * renewal" encounter with a one-line note. Each medication renews the item it
 * was last signed as here, when there is one.
 *
 * Change and Cancel sit on each medication of a signed prescription (the
 * visit's signed rows, the profile's Visits list), and Change beside Refill on
 * a chart medication prescribed here (owner decisions 2026-10-14). A change
 * signs a new prescription replacing the old medication, the way a refill
 * signs; a cancel records a reason and may stop the medication on the chart.
 *
 * The provider is mounted by the profile page and the Ice Blue encounter; rows
 * render their controls only inside it and only for a physician, so every
 * other surface reading the chart is unchanged. Inside a visit, a medication
 * the visit already prescribed (or refilled) is not offered again: a second
 * prescription for it would be a duplicate. The dialogs, with the Rx form and
 * PDF viewer they need, load only when one is opened (MedicationRefillDialog.tsx,
 * PrescriptionCancelDialog.tsx).
 */
import type {
  MedicationRefillCandidate,
  PrintablePrescriptionCancellation,
  PrintablePrescriptionItemState,
} from "@emr/contracts";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useContext,
  useMemo,
  useState,
} from "react";
import "../medication-refill.css";
import type { createAgentClient } from "../api/agentClient";
import type {
  PrescriptionChangeReceipt,
  PrescriptionRenewalReceipt,
  PrintablePrescriptionRecord,
} from "../api/clinicalWorkspace";
import {
  type PrintablePrescriptionPatient,
  prescriptionItemState,
  prescriptionItemsOf,
  prescriptionMedicationNames,
} from "../lib/printablePrescription";
import type { ProviderSignatureImageLoader } from "../lib/providerSignatureImage";
import type { FdbMedicationSearch, FdbMonograph, FdbScreen } from "./FdbPrescribing";
import { cancelReasonLabel, type SignedItemPrefill } from "./medicationRefillValues";
import type { PrintablePrescriptionClient } from "./usePrintablePrescriptionSigning";

const MedicationRefillDialog = lazy(() => import("./MedicationRefillDialog"));
const PrescriptionChangeDialog = lazy(() =>
  import("./MedicationRefillDialog").then((module) => ({
    default: module.PrescriptionChangeDialog,
  })),
);
const PrescriptionCancelDialog = lazy(() => import("./PrescriptionCancelDialog"));

export type MedicationRefillClient = PrintablePrescriptionClient &
  Pick<
    ReturnType<typeof createAgentClient>,
    "listMedicationRefillCandidates" | "signPrescriptionRenewal" | "screenPatientFdbPrescription"
  > &
  Partial<
    Pick<
      ReturnType<typeof createAgentClient>,
      "signPrescriptionChange" | "cancelPrintablePrescription"
    >
  >;

/** A refill signed inside a visit lands on that visit. */
export interface MedicationRefillVisit {
  readonly encounterId: string;
  /** The note is signed: the refill files as a document only, with no Plan line. */
  readonly noteSigned: boolean;
  /** The visit's FDB screening, when the runtime has FDB content. */
  readonly fdbScreen?: FdbScreen | undefined;
  /** Files the signed refill with the visit; planLines is empty when nothing goes in the Plan. */
  readonly onSigned: (record: PrintablePrescriptionRecord, planLines: readonly string[]) => void;
  /** Files a change signed in the visit (EXECDEV-176), the way a refill files. */
  readonly onChangeSigned?:
    | ((record: PrintablePrescriptionRecord, planLines: readonly string[]) => void)
    | undefined;
  /** Records a cancellation made in the visit; planLines is empty when nothing goes in the Plan. */
  readonly onCancelled?:
    | ((receipt: PrintablePrescriptionCancellation, planLines: readonly string[]) => void)
    | undefined;
  /**
   * A prescription this visit signed, with what became of each medication: a change opened on a
   * chart medication then offers to re-issue the prescription's other medications with it.
   */
  readonly signedPrescription?:
    | ((printablePrescriptionId: string) =>
        | {
            readonly record: PrintablePrescriptionRecord;
            readonly states: readonly PrintablePrescriptionItemState[];
          }
        | undefined)
    | undefined;
}

export interface MedicationRefillSetup {
  readonly client: MedicationRefillClient;
  readonly siteId: string;
  readonly patient: PrintablePrescriptionPatient;
  readonly loadSignatureImage?: ProviderSignatureImageLoader | undefined;
  /** FDB coded prescribing; absent where the runtime has no FDB content. */
  readonly fdb?:
    | { readonly search: FdbMedicationSearch; readonly monograph: FdbMonograph }
    | undefined;
  /** Inside a visit; absent on the profile, where a refill opens a renewal encounter. */
  readonly visit?: MedicationRefillVisit | undefined;
  /** Outside a visit: the renewal was signed, so the chart and its visits changed. */
  readonly onRenewalSigned?: ((receipt: PrescriptionRenewalReceipt) => void) | undefined;
  /** Outside a visit: the change was signed on a "Prescription change" encounter. */
  readonly onChangeSigned?: ((receipt: PrescriptionChangeReceipt) => void) | undefined;
  /** Outside a visit: medications were cancelled, perhaps stopped on the chart. */
  readonly onCancelled?: ((receipt: PrintablePrescriptionCancellation) => void) | undefined;
}

/** A signed medication a change starts from: the item as it was signed, and where. */
export interface PrescriptionChangeSource {
  readonly printablePrescriptionId: string;
  readonly itemIndex: number;
  readonly encounterId: string;
  readonly signedAt: string;
  readonly item: SignedItemPrefill;
}

/** What the change dialog opens on. */
export type PrescriptionChangeRequest =
  | {
      /** The medications chosen to change, each from its signed prescription. */
      readonly kind: "signed";
      readonly sources: readonly PrescriptionChangeSource[];
      /**
       * The other active medications of the same prescription, offered to re-issue on the new
       * one: a signed paper cannot lose one line, so a visit fixing one medication usually
       * re-issues the rest with it.
       */
      readonly alsoOnPrescription: readonly PrescriptionChangeSource[];
    }
  | {
      /** A chart medication: changes the item it was last signed as here. */
      readonly kind: "medication";
      readonly medicationId: string;
    };

/** What the cancel dialog opens on: a signed prescription and the medication chosen. */
export interface PrescriptionCancelRequest {
  readonly record: PrintablePrescriptionRecord;
  readonly itemIndex: number;
  readonly states: readonly PrintablePrescriptionItemState[];
}

type DialogRequest =
  | { readonly kind: "refill"; readonly medicationIds: readonly string[] }
  | { readonly kind: "change"; readonly request: PrescriptionChangeRequest }
  | { readonly kind: "cancel"; readonly request: PrescriptionCancelRequest };

interface MedicationRefillContextValue {
  readonly inVisit: boolean;
  /** Chart medications this visit already prescribed or refilled: not offered again. */
  readonly prescribedThisVisit: ReadonlySet<string>;
  /** Inside a visit: chart medications with a signed prescription here to change. */
  readonly changeableInVisit: ReadonlySet<string> | null;
  readonly selected: ReadonlySet<string>;
  readonly toggle: (medicationId: string) => void;
  readonly clearSelection: () => void;
  /** Opens the dialog for these chart medications (current versions), in this order. */
  readonly open: (medicationIds: readonly string[]) => void;
  /** Changing and cancelling need the runtime's change client (EXECDEV-176). */
  readonly canChange: boolean;
  readonly openChange: (request: PrescriptionChangeRequest) => void;
  readonly openCancel: (request: PrescriptionCancelRequest) => void;
  /** The candidates query, for a surface that lists them itself (the encounter panel). */
  readonly setup: MedicationRefillSetup;
}

const MedicationRefillContext = createContext<MedicationRefillContextValue | null>(null);

/** The refill controls' state, or null where refills are not offered. */
export function useMedicationRefill(): MedicationRefillContextValue | null {
  return useContext(MedicationRefillContext);
}

export const medicationRefillCandidatesKey = (siteId: string, patientId: string) =>
  ["medication-refill-candidates", siteId, patientId] as const;

/**
 * The active chart medications with the item each would renew. Every read is
 * audited, so it is read when needed (the dialog, a visit) and not on window
 * focus.
 */
export function useMedicationRefillCandidates(setup: MedicationRefillSetup | null, enabled = true) {
  return useQuery({
    queryKey:
      setup === null
        ? ["medication-refill-candidates"]
        : medicationRefillCandidatesKey(setup.siteId, setup.patient.patientId),
    queryFn: ({ signal }) => {
      const { client, siteId, patient } = setup as MedicationRefillSetup;
      return client.listMedicationRefillCandidates(siteId, patient.patientId, signal);
    },
    enabled: setup !== null && enabled,
    refetchOnWindowFocus: false,
  });
}

/** A candidate this visit already prescribed or refilled: its last prescription is the visit's. */
export function prescribedInVisit(
  candidate: MedicationRefillCandidate,
  visit: MedicationRefillVisit | undefined,
): boolean {
  return visit !== undefined && candidate.lastPrescription?.encounterId === visit.encounterId;
}

/** The change a chart medication's last signed prescription starts from, when it has one. */
export function changeSourceOfCandidate(
  candidate: MedicationRefillCandidate,
): PrescriptionChangeSource | undefined {
  const last = candidate.lastPrescription;
  return last === undefined
    ? undefined
    : {
        printablePrescriptionId: last.printablePrescriptionId,
        itemIndex: last.itemIndex,
        encounterId: last.encounterId,
        signedAt: last.signedAt,
        item: last.item,
      };
}

/** The other medications of a signed record still standing, as a change re-issues them. */
export function otherStandingSources(
  record: PrintablePrescriptionRecord,
  states: readonly PrintablePrescriptionItemState[],
  itemIndex: number,
): PrescriptionChangeSource[] {
  return prescriptionMedicationNames(record).flatMap((_name, otherIndex) => {
    if (otherIndex === itemIndex) return [];
    const state = prescriptionItemState(states, otherIndex);
    if (state !== undefined && state.status !== "active") return [];
    const source = changeSourceOfRecord(record, otherIndex);
    return source === undefined ? [] : [source];
  });
}

/** A medication of a signed record as a change starts from it. */
export function changeSourceOfRecord(
  record: PrintablePrescriptionRecord,
  itemIndex: number,
): PrescriptionChangeSource | undefined {
  const item = prescriptionItemsOf(record.prescription)[itemIndex];
  return item === undefined
    ? undefined
    : {
        printablePrescriptionId: record.printablePrescriptionId,
        itemIndex,
        encounterId: record.encounterId,
        signedAt: record.signedAt,
        item,
      };
}

export function MedicationRefillProvider({
  setup,
  hidden = false,
  children,
}: {
  /** Null where refills are not offered (no persisted chart, no prescribing client). */
  readonly setup: MedicationRefillSetup | null;
  /** The keep-alive tab pane is hidden: the dialog, which portals, waits. */
  readonly hidden?: boolean;
  readonly children: ReactNode;
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [dialog, setDialog] = useState<DialogRequest | null>(null);
  // Only a physician signs a prescription; the shared profile query decides.
  const profile = useQuery({
    queryKey: ["workforce-profile"],
    queryFn: ({ signal }) => (setup as MedicationRefillSetup).client.getWorkforceProfile(signal),
    enabled: setup !== null,
  });
  const physician = profile.data?.role === "physician";
  // Inside a visit the list is read up front, to know what the visit already
  // prescribed; the visit's prescription client reads it again after each
  // signing (PersistedHorizonEncounter).
  const visit = setup?.visit;
  const candidates = useMedicationRefillCandidates(setup, physician && visit !== undefined);
  const prescribedThisVisit = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        (candidates.data?.items ?? [])
          .filter((candidate) => prescribedInVisit(candidate, visit))
          .map((candidate) => candidate.medicationId),
      ),
    [candidates.data, visit],
  );
  const changeableInVisit = useMemo<ReadonlySet<string> | null>(
    () =>
      visit === undefined || candidates.data === undefined
        ? null
        : new Set(
            candidates.data.items
              .filter((candidate) => candidate.lastPrescription !== undefined)
              .map((candidate) => candidate.medicationId),
          ),
    [candidates.data, visit],
  );
  const canChange =
    setup?.client.signPrescriptionChange !== undefined &&
    setup.client.cancelPrintablePrescription !== undefined;
  const value = useMemo<MedicationRefillContextValue | null>(
    () =>
      setup === null || !physician
        ? null
        : {
            inVisit: setup.visit !== undefined,
            prescribedThisVisit,
            changeableInVisit,
            selected,
            setup,
            canChange,
            toggle: (medicationId) =>
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(medicationId)) next.delete(medicationId);
                else next.add(medicationId);
                return next;
              }),
            clearSelection: () => setSelected(new Set()),
            open: (medicationIds) =>
              setDialog({ kind: "refill", medicationIds: [...new Set(medicationIds)] }),
            openChange: (request) => setDialog({ kind: "change", request }),
            openCancel: (request) => setDialog({ kind: "cancel", request }),
          },
    [canChange, changeableInVisit, physician, prescribedThisVisit, selected, setup],
  );
  const close = () => setDialog(null);
  return (
    <MedicationRefillContext.Provider value={value}>
      {children}
      {value !== null && dialog !== null && !hidden ? (
        <Suspense fallback={null}>
          {dialog.kind === "refill" ? (
            <MedicationRefillDialog
              setup={value.setup}
              medicationIds={dialog.medicationIds}
              onClose={close}
              onSigned={() => setSelected(new Set())}
            />
          ) : dialog.kind === "change" ? (
            <PrescriptionChangeDialog
              setup={value.setup}
              request={dialog.request}
              onClose={close}
            />
          ) : (
            <PrescriptionCancelDialog
              setup={value.setup}
              request={dialog.request}
              onClose={close}
            />
          )}
        </Suspense>
      ) : null}
    </MedicationRefillContext.Provider>
  );
}

/* ---- Row controls -------------------------------------------------------- */

/** The tick that adds a medication to "Refill selected". */
export function MedicationRefillCheckbox({
  medicationId,
  display,
}: {
  readonly medicationId: string;
  readonly display: string;
}) {
  const refill = useMedicationRefill();
  if (refill === null || refill.prescribedThisVisit.has(medicationId)) return null;
  return (
    <input
      type="checkbox"
      className="refill-check"
      aria-label={`Select ${display} to refill`}
      checked={refill.selected.has(medicationId)}
      onChange={() => refill.toggle(medicationId)}
    />
  );
}

/**
 * The small Refill button beside one medication, or, for a medication this
 * visit already prescribed or refilled, a note saying so.
 */
export function MedicationRefillButton({
  medicationId,
  display,
  variant,
}: {
  readonly medicationId: string;
  readonly display: string;
  /** "profile": a profile section (pp buttons); "panel": the encounter's context panel. */
  readonly variant: "profile" | "panel";
}) {
  const refill = useMedicationRefill();
  if (refill === null) return null;
  if (refill.prescribedThisVisit.has(medicationId))
    return <span className="refill-prescribed">Prescribed this visit</span>;
  return (
    <button
      type="button"
      className={variant === "profile" ? "pp-btn ghost sm" : "ib-btn ghost sm"}
      aria-label={`Refill ${display}`}
      onClick={() => refill.open([medicationId])}
    >
      Refill
    </button>
  );
}

/**
 * Change beside Refill (EXECDEV-176), on a chart medication prescribed here: it
 * changes the item the medication was last signed as. Inside a visit the
 * chart's prescriptions are known; on the profile a medication whose chart
 * entry came from a prescription signed here is offered, and the dialog says
 * so when that prescription was cancelled since.
 */
export function MedicationChangeButton({
  medicationId,
  display,
  sourceKind,
  variant,
}: {
  readonly medicationId: string;
  readonly display: string;
  /** The chart entry's source kind, deciding outside a visit. */
  readonly sourceKind?: string | undefined;
  readonly variant: "profile" | "panel";
}) {
  const refill = useMedicationRefill();
  if (refill === null || !refill.canChange) return null;
  const changeable =
    refill.changeableInVisit !== null
      ? refill.changeableInVisit.has(medicationId)
      : !refill.inVisit && sourceKind === "clinician_prescribed";
  if (!changeable) return null;
  return (
    <button
      type="button"
      className={variant === "profile" ? "pp-btn ghost sm" : "ib-btn ghost sm"}
      aria-label={`Change ${display}`}
      onClick={() => refill.openChange({ kind: "medication", medicationId })}
    >
      Change
    </button>
  );
}

/**
 * "Refill selected (n)" once anything is ticked, for the medications still
 * listed on this surface.
 */
export function MedicationRefillSelectedButton({
  listedIds,
  variant,
}: {
  readonly listedIds: readonly string[];
  readonly variant: "profile" | "panel";
}) {
  const refill = useMedicationRefill();
  if (refill === null) return null;
  const ids = listedIds.filter(
    (id) => refill.selected.has(id) && !refill.prescribedThisVisit.has(id),
  );
  if (ids.length === 0) return null;
  return (
    <button
      type="button"
      className={variant === "profile" ? "pp-btn primary sm" : "ib-btn primary sm"}
      onClick={() => refill.open(ids)}
    >
      Refill selected ({ids.length})
    </button>
  );
}

/* ---- Signed prescriptions ------------------------------------------------ */

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

/** "Cancelled Oct 14 — adverse reaction", "Changed Oct 14": what became of a medication. */
export function prescriptionItemStatusText(state: PrintablePrescriptionItemState): string | null {
  const cancellation = state.cancellation;
  if (state.status === "active" || cancellation === undefined) return null;
  const when = shortDate(cancellation.cancelledAt);
  if (cancellation.reasonCode === "changed") return `Changed ${when}`;
  return `Cancelled ${when} — ${cancelReasonLabel(cancellation.reasonCode, cancellation.reasonText)}`;
}

/**
 * What became of the medications of a signed prescription, and Change and
 * Cancel on each still active (EXECDEV-176). "inline" sits in a row that shows
 * one medication (a visit's signed card, a one-medication prescription);
 * "lines" lists each medication of a prescription by name. Change and Cancel
 * show only to a physician, inside the refill provider, on a verified record;
 * what became of a medication shows to everyone who sees the record.
 */
export function SignedPrescriptionItemControls({
  record,
  states,
  itemIndexes,
  layout,
  variant,
}: {
  readonly record: PrintablePrescriptionRecord;
  readonly states: readonly PrintablePrescriptionItemState[];
  /** The medications this row stands for; every medication of the record when absent. */
  readonly itemIndexes?: readonly number[] | undefined;
  readonly layout: "inline" | "lines";
  readonly variant: "profile" | "encounter";
}) {
  const refill = useMedicationRefill();
  // Names from every entry the record lists, so an older record still says what became of
  // each; only a medication that reads as a whole signed item can be changed.
  const names = prescriptionMedicationNames(record);
  const indexes = itemIndexes ?? names.map((_name, index) => index);
  const actionable = refill?.canChange === true && record.verification === "verified";
  const button = variant === "profile" ? "pp-btn ghost sm" : "ib-btn ghost sm";
  const rows = indexes.flatMap((itemIndex) => {
    const name = names[itemIndex] ?? prescriptionItemState(states, itemIndex)?.medicationDisplay;
    if (name === undefined) return [];
    const state = prescriptionItemState(states, itemIndex);
    const status = state === undefined ? null : prescriptionItemStatusText(state);
    const active = state === undefined || state.status === "active";
    if (!active && status === null) return [];
    const source = changeSourceOfRecord(record, itemIndex);
    return [
      {
        itemIndex,
        name,
        status,
        state,
        actions:
          actionable && active && source !== undefined ? (
            <span className="rx-item-actions">
              <button
                type="button"
                className={button}
                aria-label={`Change ${name}`}
                onClick={() =>
                  refill.openChange({
                    kind: "signed",
                    sources: [source],
                    alsoOnPrescription: otherStandingSources(record, states, itemIndex),
                  })
                }
              >
                Change
              </button>
              <button
                type="button"
                className={button}
                aria-label={`Cancel ${name}`}
                onClick={() => refill.openCancel({ record, itemIndex, states })}
              >
                Cancel
              </button>
            </span>
          ) : null,
      },
    ];
  });
  if (rows.every((row) => row.status === null && row.actions === null)) return null;
  if (layout === "inline") {
    return (
      <div className={`rx-item-controls inline ${variant}`}>
        {rows.map((row) => (
          <span key={row.itemIndex} className="rx-item-control">
            {row.status === null ? null : (
              <span className={`rx-item-status ${row.state?.status ?? "active"}`}>
                {row.status}
              </span>
            )}
            {row.actions}
          </span>
        ))}
      </div>
    );
  }
  return (
    <ul
      className={`rx-item-controls lines ${variant}`}
      aria-label="Medications on this prescription"
    >
      {rows.map((row) => (
        <li key={row.itemIndex} className={`rx-item-control ${row.state?.status ?? "active"}`}>
          <span className="rx-item-name">{row.name}</span>
          {row.status === null ? null : (
            <span className={`rx-item-status ${row.state?.status ?? "active"}`}>{row.status}</span>
          )}
          {row.actions}
        </li>
      ))}
    </ul>
  );
}
